import { useState, useEffect, useMemo } from 'react'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

// Chart colors
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16']

interface UnbilledUsageItem {
  customerId: string
  customerName: string
  subscriptionId: string
  subscriptionName: string
  productName: string
  meterCategory?: string
  chargeType: string
  quantity: number
  unitPrice: number
  totalAmount: number
  currency: string
  chargeStartDate: string
  chargeEndDate: string
  billingCycleType: string
}

interface BilledUsageItem {
  invoiceId: string
  customerId: string
  customerName: string
  subscriptionId: string
  subscriptionName?: string
  productName: string
  meterCategory?: string
  chargeType: string
  quantity: number
  unitPrice: number
  totalAmount: number
  currency: string
  chargeStartDate: string
  chargeEndDate: string
}

interface Invoice {
  id: string
  invoiceDate: string
  billingPeriodStartDate?: string
  billingPeriodEndDate?: string
  totalCharges?: number
  currencyCode?: string
}

interface AzureBillingData {
  unbilled?: {
    items: UnbilledUsageItem[]
    totalAmount: number
    periodStart: string
    periodEnd: string
    lastSyncedAt?: string
    isStale?: boolean
  }
  billed?: {
    items: BilledUsageItem[]
    totalAmount: number
    invoiceId: string
    invoiceDate: string
    billingPeriodStart?: string
    billingPeriodEnd?: string
  }
  availableInvoices?: Invoice[]
}

interface CategoryGroup {
  category: string
  totalAmount: number
  itemCount: number
  totalQuantity: number
}

interface Customer {
  id: string
  name: string
}

interface Subscription {
  id: string
  name: string
}

export default function AzureBilling() {
  const [loadingUnbilled, setLoadingUnbilled] = useState(true)
  const [loadingBilled, setLoadingBilled] = useState(false)
  const [syncingUnbilled, setSyncingUnbilled] = useState(false)
  const [errorUnbilled, setErrorUnbilled] = useState<string | null>(null)
  const [errorBilled, setErrorBilled] = useState<string | null>(null)
  const [unbilledData, setUnbilledData] = useState<AzureBillingData['unbilled'] | null>(null)
  const [billedData, setBilledData] = useState<AzureBillingData['billed'] | null>(null)
  const [availableInvoices, setAvailableInvoices] = useState<Invoice[]>([])
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'unbilled' | 'billed'>('unbilled')

  // Customer filter state (unbilled)
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)

  // Customer filter state (billed)
  const [selectedBilledCustomerId, setSelectedBilledCustomerId] = useState<string>('')
  const [billedCustomerSearch, setBilledCustomerSearch] = useState('')
  const [showBilledCustomerDropdown, setShowBilledCustomerDropdown] = useState(false)

  // Subscription filter state (unbilled)
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>('')
  const [subscriptionSearch, setSubscriptionSearch] = useState('')
  const [showSubscriptionDropdown, setShowSubscriptionDropdown] = useState(false)

  // Subscription filter state (billed)
  const [selectedBilledSubscriptionId, setSelectedBilledSubscriptionId] = useState<string>('')
  const [billedSubscriptionSearch, setBilledSubscriptionSearch] = useState('')
  const [showBilledSubscriptionDropdown, setShowBilledSubscriptionDropdown] = useState(false)

  const { getAccessToken } = useAuth()

  // Load unbilled data on mount
  useEffect(() => {
    loadUnbilledData()
  }, [])

  // Load billed data when invoice selection changes
  useEffect(() => {
    if (selectedInvoiceId) {
      loadBilledData(selectedInvoiceId)
    }
  }, [selectedInvoiceId])

  // Reset subscription filter when customer changes (unbilled)
  useEffect(() => {
    setSelectedSubscriptionId('')
  }, [selectedCustomerId])

  // Reset subscription filter when customer changes (billed)
  useEffect(() => {
    setSelectedBilledSubscriptionId('')
  }, [selectedBilledCustomerId])

  const loadUnbilledData = async () => {
    try {
      setLoadingUnbilled(true)
      setErrorUnbilled(null)
      const token = await getAccessToken()
      // Fetch both unbilled and billed data - unbilled now comes from database (synced periodically)
      const result = await api.getAzureBilling(token, { view: 'both' })
      setUnbilledData(result.unbilled || null)
      setBilledData(result.billed || null)

      // Set available invoices
      if (result.availableInvoices && result.availableInvoices.length > 0) {
        setAvailableInvoices(result.availableInvoices)
        if (result.billed?.invoiceId) {
          setSelectedInvoiceId(result.billed.invoiceId)
        } else {
          setSelectedInvoiceId(result.availableInvoices[0].id)
        }
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err)
      setErrorUnbilled('Failed to load billing data. Please try again.')
    } finally {
      setLoadingUnbilled(false)
    }
  }

  const syncUnbilledData = async () => {
    try {
      setSyncingUnbilled(true)
      const token = await getAccessToken()
      await api.syncUnbilledUsage(token)
      // Reload data after sync
      await loadUnbilledData()
    } catch (err) {
      console.error('Failed to sync unbilled data:', err)
      setErrorUnbilled('Failed to sync unbilled usage data. Please try again.')
    } finally {
      setSyncingUnbilled(false)
    }
  }

  // Format relative time
  const formatRelativeTime = (dateString: string | undefined) => {
    if (!dateString) return 'Never'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
    return date.toLocaleDateString()
  }

  const loadBilledData = async (invoiceId: string) => {
    try {
      setLoadingBilled(true)
      setErrorBilled(null)
      const token = await getAccessToken()
      const result = await api.getAzureBilling(token, { view: 'billed', invoiceId })
      setBilledData(result.billed || null)
    } catch (err) {
      console.error('Failed to fetch billed data:', err)
      setErrorBilled('Failed to load billed usage data. Please try again.')
    } finally {
      setLoadingBilled(false)
    }
  }

  // Extract unique customers from unbilled data
  const customers = useMemo((): Customer[] => {
    if (!unbilledData?.items) return []
    const customerMap = new Map<string, Customer>()
    unbilledData.items.forEach(item => {
      if (item.customerId && !customerMap.has(item.customerId)) {
        customerMap.set(item.customerId, {
          id: item.customerId,
          name: item.customerName || 'Unknown Customer'
        })
      }
    })
    return Array.from(customerMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [unbilledData])

  // Filter customers by search
  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers
    const search = customerSearch.toLowerCase()
    return customers.filter(c => c.name.toLowerCase().includes(search))
  }, [customers, customerSearch])

  // Extract unique subscriptions from unbilled data (filtered by customer if selected)
  const subscriptions = useMemo((): Subscription[] => {
    if (!unbilledData?.items) return []
    const items = selectedCustomerId
      ? unbilledData.items.filter(i => i.customerId === selectedCustomerId)
      : unbilledData.items
    const subMap = new Map<string, Subscription>()
    items.forEach(item => {
      if (item.subscriptionId && !subMap.has(item.subscriptionId)) {
        subMap.set(item.subscriptionId, {
          id: item.subscriptionId,
          name: item.subscriptionName || item.subscriptionId
        })
      }
    })
    return Array.from(subMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [unbilledData, selectedCustomerId])

  // Filter subscriptions by search
  const filteredSubscriptions = useMemo(() => {
    if (!subscriptionSearch) return subscriptions
    const search = subscriptionSearch.toLowerCase()
    return subscriptions.filter(s => s.name.toLowerCase().includes(search))
  }, [subscriptions, subscriptionSearch])

  // Get selected subscription name
  const selectedSubscriptionName = useMemo(() => {
    if (!selectedSubscriptionId) return 'All Subscriptions'
    const subscription = subscriptions.find(s => s.id === selectedSubscriptionId)
    return subscription?.name || 'Unknown'
  }, [selectedSubscriptionId, subscriptions])

  // Filter unbilled items by selected customer and subscription
  const filteredUnbilledItems = useMemo(() => {
    if (!unbilledData?.items) return []
    let items = unbilledData.items
    if (selectedCustomerId) {
      items = items.filter(item => item.customerId === selectedCustomerId)
    }
    if (selectedSubscriptionId) {
      items = items.filter(item => item.subscriptionId === selectedSubscriptionId)
    }
    return items
  }, [unbilledData, selectedCustomerId, selectedSubscriptionId])

  // Group items by category
  const groupByCategory = (items: UnbilledUsageItem[]): CategoryGroup[] => {
    const grouped = items.reduce((acc, item) => {
      const category = item.meterCategory || 'Other'
      if (!acc[category]) {
        acc[category] = {
          category,
          totalAmount: 0,
          itemCount: 0,
          totalQuantity: 0,
        }
      }
      acc[category].totalAmount += item.totalAmount
      acc[category].itemCount += 1
      acc[category].totalQuantity += item.quantity
      return acc
    }, {} as Record<string, CategoryGroup>)

    return Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Get category groups for display
  const categoryGroups = useMemo(() => {
    return groupByCategory(filteredUnbilledItems)
  }, [filteredUnbilledItems])

  // Calculate filtered totals
  const filteredTotal = useMemo(() => {
    return filteredUnbilledItems.reduce((sum, item) => sum + item.totalAmount, 0)
  }, [filteredUnbilledItems])

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  // Get category icon/color
  const getCategoryStyle = (category: string) => {
    const styles: Record<string, { bg: string; text: string; icon: string }> = {
      'Compute': { bg: 'bg-blue-100', text: 'text-blue-800', icon: '💻' },
      'Storage': { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '💾' },
      'Networking': { bg: 'bg-green-100', text: 'text-green-800', icon: '🌐' },
      'Database': { bg: 'bg-purple-100', text: 'text-purple-800', icon: '🗄️' },
      'Virtual Machines': { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🖥️' },
      'Backup': { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: '📦' },
      'Security': { bg: 'bg-red-100', text: 'text-red-800', icon: '🔒' },
      'Management': { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⚙️' },
      'AI': { bg: 'bg-pink-100', text: 'text-pink-800', icon: '🤖' },
      'Analytics': { bg: 'bg-cyan-100', text: 'text-cyan-800', icon: '📊' },
    }
    return styles[category] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: '📁' }
  }

  // Get selected customer name
  const selectedCustomerName = useMemo(() => {
    if (!selectedCustomerId) return 'All Customers'
    const customer = customers.find(c => c.id === selectedCustomerId)
    return customer?.name || 'Unknown'
  }, [selectedCustomerId, customers])

  // Sort invoices by date descending
  const sortedInvoices = useMemo(() => {
    return [...availableInvoices].sort((a, b) => {
      const dateA = new Date(a.invoiceDate || 0).getTime()
      const dateB = new Date(b.invoiceDate || 0).getTime()
      return dateB - dateA
    })
  }, [availableInvoices])

  // Extract unique customers from billed data
  const billedCustomers = useMemo((): Customer[] => {
    if (!billedData?.items) return []
    const customerMap = new Map<string, Customer>()
    billedData.items.forEach(item => {
      if (item.customerId && !customerMap.has(item.customerId)) {
        customerMap.set(item.customerId, {
          id: item.customerId,
          name: item.customerName || 'Unknown Customer'
        })
      }
    })
    return Array.from(customerMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [billedData])

  // Filter billed customers by search
  const filteredBilledCustomers = useMemo(() => {
    if (!billedCustomerSearch) return billedCustomers
    const search = billedCustomerSearch.toLowerCase()
    return billedCustomers.filter(c => c.name.toLowerCase().includes(search))
  }, [billedCustomers, billedCustomerSearch])

  // Extract unique subscriptions from billed data (filtered by customer if selected)
  const billedSubscriptions = useMemo((): Subscription[] => {
    if (!billedData?.items) return []
    const items = selectedBilledCustomerId
      ? billedData.items.filter(i => i.customerId === selectedBilledCustomerId)
      : billedData.items
    const subMap = new Map<string, Subscription>()
    items.forEach(item => {
      if (item.subscriptionId && !subMap.has(item.subscriptionId)) {
        subMap.set(item.subscriptionId, {
          id: item.subscriptionId,
          name: item.subscriptionName || item.subscriptionId
        })
      }
    })
    return Array.from(subMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    )
  }, [billedData, selectedBilledCustomerId])

  // Filter billed subscriptions by search
  const filteredBilledSubscriptions = useMemo(() => {
    if (!billedSubscriptionSearch) return billedSubscriptions
    const search = billedSubscriptionSearch.toLowerCase()
    return billedSubscriptions.filter(s => s.name.toLowerCase().includes(search))
  }, [billedSubscriptions, billedSubscriptionSearch])

  // Get selected billed subscription name
  const selectedBilledSubscriptionName = useMemo(() => {
    if (!selectedBilledSubscriptionId) return 'All Subscriptions'
    const subscription = billedSubscriptions.find(s => s.id === selectedBilledSubscriptionId)
    return subscription?.name || 'Unknown'
  }, [selectedBilledSubscriptionId, billedSubscriptions])

  // Filter billed items by selected customer and subscription
  const filteredBilledItems = useMemo(() => {
    if (!billedData?.items) return []
    let items = billedData.items
    if (selectedBilledCustomerId) {
      items = items.filter(item => item.customerId === selectedBilledCustomerId)
    }
    if (selectedBilledSubscriptionId) {
      items = items.filter(item => item.subscriptionId === selectedBilledSubscriptionId)
    }
    return items
  }, [billedData, selectedBilledCustomerId, selectedBilledSubscriptionId])

  // Group billed items by category
  const groupBilledByCategory = (items: BilledUsageItem[]): CategoryGroup[] => {
    const grouped = items.reduce((acc, item) => {
      const category = item.meterCategory || 'Other'
      if (!acc[category]) {
        acc[category] = {
          category,
          totalAmount: 0,
          itemCount: 0,
          totalQuantity: 0,
        }
      }
      acc[category].totalAmount += item.totalAmount
      acc[category].itemCount += 1
      acc[category].totalQuantity += item.quantity
      return acc
    }, {} as Record<string, CategoryGroup>)

    return Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount)
  }

  // Get billed category groups
  const billedCategoryGroups = useMemo(() => {
    return groupBilledByCategory(filteredBilledItems)
  }, [filteredBilledItems])

  // Calculate filtered billed totals
  const filteredBilledTotal = useMemo(() => {
    return filteredBilledItems.reduce((sum, item) => sum + item.totalAmount, 0)
  }, [filteredBilledItems])

  // Get selected billed customer name
  const selectedBilledCustomerName = useMemo(() => {
    if (!selectedBilledCustomerId) return 'All Customers'
    const customer = billedCustomers.find(c => c.id === selectedBilledCustomerId)
    return customer?.name || 'Unknown'
  }, [selectedBilledCustomerId, billedCustomers])

  // Group billed items by customer for summary
  const groupBilledByCustomer = (items: BilledUsageItem[]) => {
    const grouped = items.reduce((acc, item) => {
      const key = item.customerId
      if (!acc[key]) {
        acc[key] = {
          customerId: item.customerId,
          customerName: item.customerName,
          totalAmount: 0,
          itemCount: 0,
        }
      }
      acc[key].totalAmount += item.totalAmount
      acc[key].itemCount += 1
      return acc
    }, {} as Record<string, { customerId: string; customerName: string; totalAmount: number; itemCount: number }>)

    return Object.values(grouped).sort((a, b) => b.totalAmount - a.totalAmount)
  }

  return (
    <div className="px-4 sm:px-0 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Azure Billing</h2>
          <p className="mt-1 text-sm text-gray-600">
            View current unbilled Azure consumption and historical invoiced usage.
          </p>
        </div>
        <button
          onClick={loadUnbilledData}
          disabled={loadingUnbilled}
          className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
        >
          <svg className={`h-4 w-4 mr-2 ${loadingUnbilled ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Tabbed Container */}
      <div className="bg-white shadow rounded-lg">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {/* Current Period Tab */}
            <button
              onClick={() => setActiveTab('unbilled')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'unbilled'
                  ? 'border-orange-500 text-orange-600 bg-orange-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-3">
                <div className={`flex-shrink-0 rounded-md p-1.5 ${activeTab === 'unbilled' ? 'bg-orange-100' : 'bg-gray-100'}`}>
                  <svg className={`h-5 w-5 ${activeTab === 'unbilled' ? 'text-orange-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Current Period</div>
                  <div className="text-xs text-gray-500">
                    {unbilledData?.periodStart && unbilledData?.periodEnd
                      ? `${formatDate(unbilledData.periodStart)} - ${formatDate(unbilledData.periodEnd)}`
                      : 'Unbilled usage'}
                  </div>
                </div>
                {loadingUnbilled || syncingUnbilled ? (
                  <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                ) : unbilledData?.items && unbilledData.items.length > 0 ? (
                  <span className={`ml-2 px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                    activeTab === 'unbilled' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {formatCurrency(unbilledData.totalAmount || 0)}
                  </span>
                ) : (
                  <span className={`ml-2 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    activeTab === 'unbilled' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-400'
                  }`}>
                    No data
                  </span>
                )}
              </div>
            </button>

            {/* Invoiced Tab */}
            <button
              onClick={() => setActiveTab('billed')}
              className={`flex-1 px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'billed'
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center space-x-3">
                <div className={`flex-shrink-0 rounded-md p-1.5 ${activeTab === 'billed' ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <svg className={`h-5 w-5 ${activeTab === 'billed' ? 'text-green-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold">Invoiced</div>
                  <div className="text-xs text-gray-500">
                    {billedData?.billingPeriodStart && billedData?.billingPeriodEnd
                      ? `${formatDate(billedData.billingPeriodStart)} - ${formatDate(billedData.billingPeriodEnd)}`
                      : 'Historical billed usage'}
                  </div>
                </div>
                {loadingBilled ? (
                  <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
                ) : (
                  <span className={`ml-2 px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                    activeTab === 'billed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {formatCurrency(billedData?.totalAmount || 0)}
                  </span>
                )}
              </div>
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'unbilled' ? (
          /* ==================== UNBILLED CONTENT ==================== */
          <div className="p-4">
            {errorUnbilled ? (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{errorUnbilled}</p>
                <button onClick={loadUnbilledData} className="mt-2 text-sm text-red-600 hover:underline">
                  Try again
                </button>
              </div>
            ) : loadingUnbilled ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-orange-500 border-t-transparent rounded-full"></div>
              </div>
            ) : unbilledData?.items && unbilledData.items.length > 0 ? (
              <>
                {/* Sync Status Bar */}
                <div className="mb-4 flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>
                      Last synced: <span className={unbilledData.isStale ? 'text-orange-600 font-medium' : 'text-gray-900'}>
                        {formatRelativeTime(unbilledData.lastSyncedAt)}
                      </span>
                      {unbilledData.isStale && (
                        <span className="ml-2 text-orange-600">(data may be stale)</span>
                      )}
                    </span>
                  </div>
                  <button
                    onClick={syncUnbilledData}
                    disabled={syncingUnbilled}
                    className="inline-flex items-center rounded-md bg-orange-100 px-3 py-1.5 text-sm font-medium text-orange-700 hover:bg-orange-200 disabled:opacity-50"
                  >
                    {syncingUnbilled ? (
                      <>
                        <svg className="animate-spin -ml-0.5 mr-1.5 h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Syncing...
                      </>
                    ) : (
                      <>
                        <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Sync Now
                      </>
                    )}
                  </button>
                </div>

                {/* Customer Filter */}
                <div className="mb-4 flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">Customer:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                      className="relative w-64 bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm text-gray-900"
                    >
                      <span className="block truncate">{selectedCustomerName}</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>

                    {showCustomerDropdown && (
                      <div className="absolute z-10 mt-1 w-72 bg-white shadow-lg rounded-md border border-gray-200">
                        {/* Search Input */}
                        <div className="p-2 border-b">
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {/* Customer List */}
                        <ul className="max-h-60 overflow-auto py-1">
                          <li
                            className={`px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-gray-900 ${!selectedCustomerId ? 'bg-orange-100 font-medium' : ''}`}
                            onClick={() => {
                              setSelectedCustomerId('')
                              setShowCustomerDropdown(false)
                              setCustomerSearch('')
                            }}
                          >
                            All Customers ({customers.length})
                          </li>
                          {filteredCustomers.map((customer) => (
                            <li
                              key={customer.id}
                              className={`px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-gray-900 ${selectedCustomerId === customer.id ? 'bg-orange-100 font-medium' : ''}`}
                              onClick={() => {
                                setSelectedCustomerId(customer.id)
                                setShowCustomerDropdown(false)
                                setCustomerSearch('')
                              }}
                            >
                              {customer.name}
                            </li>
                          ))}
                          {filteredCustomers.length === 0 && customerSearch && (
                            <li className="px-3 py-2 text-sm text-gray-500">No customers found</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* Subscription Filter */}
                  {subscriptions.length > 0 && (
                    <>
                      <label className="text-sm font-medium text-gray-700 ml-4">Subscription:</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowSubscriptionDropdown(!showSubscriptionDropdown)}
                          className="relative w-64 bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 text-sm text-gray-900"
                        >
                          <span className="block truncate">{selectedSubscriptionName}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </button>

                        {showSubscriptionDropdown && (
                          <div className="absolute z-10 mt-1 w-80 bg-white shadow-lg rounded-md border border-gray-200">
                            {/* Search Input */}
                            <div className="p-2 border-b">
                              <input
                                type="text"
                                placeholder="Search subscriptions..."
                                value={subscriptionSearch}
                                onChange={(e) => setSubscriptionSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            {/* Subscription List */}
                            <ul className="max-h-60 overflow-auto py-1">
                              <li
                                className={`px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-gray-900 ${!selectedSubscriptionId ? 'bg-orange-100 font-medium' : ''}`}
                                onClick={() => {
                                  setSelectedSubscriptionId('')
                                  setShowSubscriptionDropdown(false)
                                  setSubscriptionSearch('')
                                }}
                              >
                                All Subscriptions ({subscriptions.length})
                              </li>
                              {filteredSubscriptions.map((subscription) => (
                                <li
                                  key={subscription.id}
                                  className={`px-3 py-2 cursor-pointer hover:bg-orange-50 text-sm text-gray-900 ${selectedSubscriptionId === subscription.id ? 'bg-orange-100 font-medium' : ''}`}
                                  onClick={() => {
                                    setSelectedSubscriptionId(subscription.id)
                                    setShowSubscriptionDropdown(false)
                                    setSubscriptionSearch('')
                                  }}
                                >
                                  <div className="truncate">{subscription.name}</div>
                                </li>
                              ))}
                              {filteredSubscriptions.length === 0 && subscriptionSearch && (
                                <li className="px-3 py-2 text-sm text-gray-500">No subscriptions found</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Clear filters */}
                  {(selectedCustomerId || selectedSubscriptionId) && (
                    <button
                      onClick={() => {
                        setSelectedCustomerId('')
                        setSelectedSubscriptionId('')
                      }}
                      className="text-sm text-orange-600 hover:text-orange-800"
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {/* Click outside to close dropdowns */}
                {(showCustomerDropdown || showSubscriptionDropdown) && (
                  <div
                    className="fixed inset-0 z-0"
                    onClick={() => {
                      setShowCustomerDropdown(false)
                      setCustomerSearch('')
                      setShowSubscriptionDropdown(false)
                      setSubscriptionSearch('')
                    }}
                  />
                )}

                {/* Top Customers - shown when "All Customers" is selected */}
                {!selectedCustomerId && customers.length > 1 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Top Customers</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {(() => {
                        const customerTotals = unbilledData.items.reduce((acc, item) => {
                          if (!acc[item.customerId]) {
                            acc[item.customerId] = { name: item.customerName, total: 0 }
                          }
                          acc[item.customerId].total += item.totalAmount
                          return acc
                        }, {} as Record<string, { name: string; total: number }>)

                        return Object.entries(customerTotals)
                          .sort((a, b) => b[1].total - a[1].total)
                          .slice(0, 8)
                          .map(([id, data]) => (
                            <div
                              key={id}
                              className="bg-orange-50 rounded-lg p-3 cursor-pointer hover:bg-orange-100 transition-colors"
                              onClick={() => setSelectedCustomerId(id)}
                            >
                              <div className="text-sm font-medium text-gray-900 truncate">{data.name}</div>
                              <div className="text-lg font-semibold text-orange-600">{formatCurrency(data.total)}</div>
                            </div>
                          ))
                      })()}
                    </div>
                  </div>
                )}

                {/* Charts Section */}
                {categoryGroups.length > 0 && (
                  <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pie Chart - Cost Distribution */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Cost Distribution by Category</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={categoryGroups.slice(0, 8)}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="totalAmount"
                              nameKey="category"
                              label={({ category, percent }) =>
                                `${category.length > 12 ? category.slice(0, 12) + '...' : category} ${(percent * 100).toFixed(0)}%`
                              }
                              labelLine={false}
                            >
                              {categoryGroups.slice(0, 8).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number) => formatCurrency(value)}
                              labelFormatter={(label) => `Category: ${label}`}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bar Chart - Top Categories */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Top Categories by Spend</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={categoryGroups.slice(0, 6)}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                          >
                            <XAxis type="number" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                            <YAxis
                              type="category"
                              dataKey="category"
                              tick={{ fontSize: 12 }}
                              width={95}
                              tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value}
                            />
                            <Tooltip
                              formatter={(value: number) => formatCurrency(value)}
                              labelFormatter={(label) => `${label}`}
                            />
                            <Bar dataKey="totalAmount" name="Amount" radius={[0, 4, 4, 0]}>
                              {categoryGroups.slice(0, 6).map((_, index) => (
                                <Cell key={`bar-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category Summary Table */}
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categoryGroups.map((group) => {
                        const style = getCategoryStyle(group.category)
                        const percentage = filteredTotal > 0 ? (group.totalAmount / filteredTotal * 100) : 0
                        return (
                          <tr key={group.category} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium ${style.bg} ${style.text}`}>
                                  <span className="mr-1.5">{style.icon}</span>
                                  {group.category}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{group.itemCount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{group.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-sm font-medium text-orange-600 text-right">
                              {formatCurrency(group.totalAmount)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-orange-500 h-2 rounded-full"
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">Total</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          {filteredUnbilledItems.length.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">-</td>
                        <td className="px-4 py-3 text-sm font-bold text-orange-600 text-right">
                          {formatCurrency(filteredTotal)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <p className="mt-2 text-sm font-medium">No unbilled usage data available</p>
                <p className="mt-1 text-xs text-gray-400">
                  {unbilledData?.lastSyncedAt
                    ? `Last synced: ${formatRelativeTime(unbilledData.lastSyncedAt)}`
                    : 'Data has not been synced yet. Click "Sync Now" to fetch the latest data.'}
                </p>
                <button
                  onClick={syncUnbilledData}
                  disabled={syncingUnbilled}
                  className="mt-4 inline-flex items-center rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 disabled:opacity-50"
                >
                  {syncingUnbilled ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Syncing...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Sync Now
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          /* ==================== BILLED CONTENT ==================== */
          <div className="p-4">
            {/* Invoice Selector */}
            <div className="mb-4 flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700">Invoice:</label>
              <select
                value={selectedInvoiceId}
                onChange={(e) => {
                  setSelectedInvoiceId(e.target.value)
                  setSelectedBilledCustomerId('') // Reset customer filter when invoice changes
                  setSelectedBilledSubscriptionId('') // Reset subscription filter when invoice changes
                }}
                disabled={loadingBilled || sortedInvoices.length === 0}
                className="w-64 bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm text-gray-900"
              >
                {sortedInvoices.length === 0 ? (
                  <option value="">No invoices available</option>
                ) : (
                  sortedInvoices.map((invoice) => (
                    <option key={invoice.id} value={invoice.id}>
                      {formatDate(invoice.invoiceDate)} - {invoice.id}
                      {invoice.totalCharges ? ` (${formatCurrency(invoice.totalCharges)})` : ''}
                    </option>
                  ))
                )}
              </select>
              {loadingBilled && (
                <div className="animate-spin h-4 w-4 border-2 border-green-500 border-t-transparent rounded-full"></div>
              )}
            </div>

            {errorBilled ? (
              <div className="rounded-md bg-red-50 p-4">
                <p className="text-sm text-red-700">{errorBilled}</p>
                <button
                  onClick={() => selectedInvoiceId && loadBilledData(selectedInvoiceId)}
                  className="mt-2 text-sm text-red-600 hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : loadingBilled ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full"></div>
              </div>
            ) : billedData?.items && billedData.items.length > 0 ? (
              <>
                {/* Customer Filter */}
                <div className="mb-4 flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700">Customer:</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowBilledCustomerDropdown(!showBilledCustomerDropdown)}
                      className="relative w-64 bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm text-gray-900"
                    >
                      <span className="block truncate">{selectedBilledCustomerName}</span>
                      <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </span>
                    </button>

                    {showBilledCustomerDropdown && (
                      <div className="absolute z-10 mt-1 w-72 bg-white shadow-lg rounded-md border border-gray-200">
                        {/* Search Input */}
                        <div className="p-2 border-b">
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={billedCustomerSearch}
                            onChange={(e) => setBilledCustomerSearch(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {/* Customer List */}
                        <ul className="max-h-60 overflow-auto py-1">
                          <li
                            className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-900 ${!selectedBilledCustomerId ? 'bg-green-100 font-medium' : ''}`}
                            onClick={() => {
                              setSelectedBilledCustomerId('')
                              setShowBilledCustomerDropdown(false)
                              setBilledCustomerSearch('')
                            }}
                          >
                            All Customers ({billedCustomers.length})
                          </li>
                          {filteredBilledCustomers.map((customer) => (
                            <li
                              key={customer.id}
                              className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-900 ${selectedBilledCustomerId === customer.id ? 'bg-green-100 font-medium' : ''}`}
                              onClick={() => {
                                setSelectedBilledCustomerId(customer.id)
                                setShowBilledCustomerDropdown(false)
                                setBilledCustomerSearch('')
                              }}
                            >
                              {customer.name}
                            </li>
                          ))}
                          {filteredBilledCustomers.length === 0 && billedCustomerSearch && (
                            <li className="px-3 py-2 text-sm text-gray-500">No customers found</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  {/* Subscription Filter */}
                  {billedSubscriptions.length > 0 && (
                    <>
                      <label className="text-sm font-medium text-gray-700 ml-4">Subscription:</label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setShowBilledSubscriptionDropdown(!showBilledSubscriptionDropdown)}
                          className="relative w-64 bg-white border border-gray-300 rounded-md shadow-sm pl-3 pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 text-sm text-gray-900"
                        >
                          <span className="block truncate">{selectedBilledSubscriptionName}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </button>

                        {showBilledSubscriptionDropdown && (
                          <div className="absolute z-10 mt-1 w-80 bg-white shadow-lg rounded-md border border-gray-200">
                            {/* Search Input */}
                            <div className="p-2 border-b">
                              <input
                                type="text"
                                placeholder="Search subscriptions..."
                                value={billedSubscriptionSearch}
                                onChange={(e) => setBilledSubscriptionSearch(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            {/* Subscription List */}
                            <ul className="max-h-60 overflow-auto py-1">
                              <li
                                className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-900 ${!selectedBilledSubscriptionId ? 'bg-green-100 font-medium' : ''}`}
                                onClick={() => {
                                  setSelectedBilledSubscriptionId('')
                                  setShowBilledSubscriptionDropdown(false)
                                  setBilledSubscriptionSearch('')
                                }}
                              >
                                All Subscriptions ({billedSubscriptions.length})
                              </li>
                              {filteredBilledSubscriptions.map((subscription) => (
                                <li
                                  key={subscription.id}
                                  className={`px-3 py-2 cursor-pointer hover:bg-green-50 text-sm text-gray-900 ${selectedBilledSubscriptionId === subscription.id ? 'bg-green-100 font-medium' : ''}`}
                                  onClick={() => {
                                    setSelectedBilledSubscriptionId(subscription.id)
                                    setShowBilledSubscriptionDropdown(false)
                                    setBilledSubscriptionSearch('')
                                  }}
                                >
                                  <div className="truncate">{subscription.name}</div>
                                </li>
                              ))}
                              {filteredBilledSubscriptions.length === 0 && billedSubscriptionSearch && (
                                <li className="px-3 py-2 text-sm text-gray-500">No subscriptions found</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Clear filters */}
                  {(selectedBilledCustomerId || selectedBilledSubscriptionId) && (
                    <button
                      onClick={() => {
                        setSelectedBilledCustomerId('')
                        setSelectedBilledSubscriptionId('')
                      }}
                      className="text-sm text-green-600 hover:text-green-800"
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {/* Click outside to close dropdowns */}
                {(showBilledCustomerDropdown || showBilledSubscriptionDropdown) && (
                  <div
                    className="fixed inset-0 z-0"
                    onClick={() => {
                      setShowBilledCustomerDropdown(false)
                      setBilledCustomerSearch('')
                      setShowBilledSubscriptionDropdown(false)
                      setBilledSubscriptionSearch('')
                    }}
                  />
                )}

                {/* Top Customers - shown when "All Customers" is selected */}
                {!selectedBilledCustomerId && billedCustomers.length > 1 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Top Customers</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      {groupBilledByCustomer(billedData.items).slice(0, 8).map((customer) => (
                        <div
                          key={customer.customerId}
                          className="bg-green-50 rounded-lg p-3 cursor-pointer hover:bg-green-100 transition-colors"
                          onClick={() => setSelectedBilledCustomerId(customer.customerId)}
                        >
                          <div className="text-sm font-medium text-gray-900 truncate">{customer.customerName}</div>
                          <div className="text-lg font-semibold text-green-600">{formatCurrency(customer.totalAmount)}</div>
                          <div className="text-xs text-gray-500">{customer.itemCount} items</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Charts Section */}
                {billedCategoryGroups.length > 0 && (
                  <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Pie Chart - Cost Distribution */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Cost Distribution by Category</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={billedCategoryGroups.slice(0, 8)}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={90}
                              paddingAngle={2}
                              dataKey="totalAmount"
                              nameKey="category"
                              label={({ category, percent }) =>
                                `${category.length > 12 ? category.slice(0, 12) + '...' : category} ${(percent * 100).toFixed(0)}%`
                              }
                              labelLine={false}
                            >
                              {billedCategoryGroups.slice(0, 8).map((_, index) => (
                                <Cell key={`billed-cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              formatter={(value: number) => formatCurrency(value)}
                              labelFormatter={(label) => `Category: ${label}`}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Bar Chart - Top Categories */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Top Categories by Spend</h4>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={billedCategoryGroups.slice(0, 6)}
                            layout="vertical"
                            margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                          >
                            <XAxis type="number" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                            <YAxis
                              type="category"
                              dataKey="category"
                              tick={{ fontSize: 12 }}
                              width={95}
                              tickFormatter={(value) => value.length > 15 ? value.slice(0, 15) + '...' : value}
                            />
                            <Tooltip
                              formatter={(value: number) => formatCurrency(value)}
                              labelFormatter={(label) => `${label}`}
                            />
                            <Bar dataKey="totalAmount" name="Amount" radius={[0, 4, 4, 0]}>
                              {billedCategoryGroups.slice(0, 6).map((_, index) => (
                                <Cell key={`billed-bar-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category Summary Table */}
                <div className="overflow-x-auto border rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Items</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">% of Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {billedCategoryGroups.map((group) => {
                        const style = getCategoryStyle(group.category)
                        const percentage = filteredBilledTotal > 0 ? (group.totalAmount / filteredBilledTotal * 100) : 0
                        return (
                          <tr key={group.category} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="flex items-center space-x-2">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-sm font-medium ${style.bg} ${style.text}`}>
                                  <span className="mr-1.5">{style.icon}</span>
                                  {group.category}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900 text-right">{group.itemCount.toLocaleString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-600 text-right">{group.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                              {formatCurrency(group.totalAmount)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{ width: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                                <span className="text-xs text-gray-500 w-12 text-right">{percentage.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">Total</td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          {filteredBilledItems.length.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">-</td>
                        <td className="px-4 py-3 text-sm font-bold text-green-600 text-right">
                          {formatCurrency(filteredBilledTotal)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2">
                  {availableInvoices.length === 0
                    ? 'No invoices available. Run billing sync to fetch invoice data.'
                    : 'No billed Azure usage found for this invoice.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
